// GridMaterial.jsx - Unreal Engine style grid material
import { useMemo } from 'react'
import {
    Fn, vec3, vec2, fract, mix, float, step, max, abs, positionWorld, normalWorld, select,
    smoothstep, length, floor, sin, attribute, clamp
} from 'three/tsl'
import * as THREE from 'three/webgpu'
import { extend } from '@react-three/fiber'
extend(THREE)

// Night is just the blue moon directional now — the old warm ground "pools"
// (a virtual-lights emissive trick) + StreetGlow bulbs were removed for a
// cleaner, cheaper look.
//
// ── Ground under the meadow ─────────────────────────────────────────────
//
// The grass grows wherever the terrain is painted RED in vertex colours
// (see world/Meadow.jsx). Blades on bare grid floor read as grass stuck to
// concrete, so the ground underneath goes green too, over the same mask.
//
// Noise rather than a flat wash, because a solid green patch has an edge
// and the whole point of painting the mask in Blender is that the shape is
// soft. Two octaves: a broad one that makes patches, a fine one that
// breaks up the boundary so the grass fades into the ground instead of
// stopping at a line.
//
// The redness formula is duplicated from Meadow.jsx (r minus the strongest
// of g and b) because one runs in JS and one in TSL. If either changes the
// other has to follow, or the grass and the green it stands on drift apart.

// Cheap value noise. No dependency on the MaterialX node set, which is not
// guaranteed to be exported by every three build this project has run on.
const hash21 = Fn(([p]) => fract(sin(p.dot(vec2(127.1, 311.7))).mul(43758.5453)))

const valueNoise = Fn(([p]) => {
    const i = floor(p)
    const f = fract(p)
    // Smoothstep the interpolant so cells do not show as diamonds.
    const u = f.mul(f).mul(float(3).sub(f.mul(2)))
    const a = hash21(i)
    const b = hash21(i.add(vec2(1, 0)))
    const c = hash21(i.add(vec2(0, 1)))
    const d = hash21(i.add(vec2(1, 1)))
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
})

// Dark earth under the blades, and a lighter green for the patches the
// noise picks out. Both sit under the blade palette (#226922 .. #88bb66)
// so the grass still reads as the brighter thing.
const GRASS_DARK = [0.09, 0.16, 0.09]
const GRASS_LIGHT = [0.16, 0.27, 0.15]

export function useGridMaterial({ gridSize = 2, lineWidth = 0.03, bgColor = [0.45, 0.45, 0.45], lineColor = [0.85, 0.85, 0.85] } = {}) {
    const gridColorNode = useMemo(() => {
        // Unreal Engine style grid using world-space coordinates
        // gridSize = size of each grid square in world units
        // Uses dominant axis selection (not blending) for clean lines on angled surfaces
        const gridNode = Fn(() => {
            const cellSize = float(gridSize)
            const width = float(lineWidth)

            // Get world position scaled by cell size
            const worldPos = positionWorld.div(cellSize)

            // Get absolute normal to determine dominant axis
            const absNormal = abs(normalWorld)

            // Determine which axis the surface faces most (dominant axis)
            const xDominant = absNormal.x.greaterThan(absNormal.y).and(absNormal.x.greaterThan(absNormal.z))
            const yDominant = absNormal.y.greaterThan(absNormal.x).and(absNormal.y.greaterThan(absNormal.z))
            // z is dominant if neither x nor y is

            // Select UV coordinates based on dominant axis
            // X-facing: use YZ plane
            // Y-facing: use XZ plane  
            // Z-facing: use XY plane
            const gridUV = select(
                xDominant,
                vec2(worldPos.y, worldPos.z),
                select(
                    yDominant,
                    vec2(worldPos.x, worldPos.z),
                    vec2(worldPos.x, worldPos.y)
                )
            )

            // Get position within each cell (0-1)
            const f = fract(gridUV)

            // Create lines at the edges of each cell
            const line = max(step(f.x, width), step(f.y, width))

            // Colors
            const bg = vec3(bgColor[0], bgColor[1], bgColor[2])
            const ln = vec3(lineColor[0], lineColor[1], lineColor[2])

            // How painted this bit of ground is. Same formula as the
            // scatter: pure red 1, white 0, so unpainted floor is
            // untouched and only the mask goes green.
            const vc = attribute('color', 'vec4')
            const red = clamp(vc.x.sub(max(vc.y, vc.z)), 0, 1)

            // Two octaves over world XZ. The broad one makes patches, the
            // fine one roughens the edge so the mask boundary is not a
            // visible line on the floor.
            const broad = valueNoise(positionWorld.xz.mul(0.09))
            const fine = valueNoise(positionWorld.xz.mul(0.42))
            const n = broad.mul(0.7).add(fine.mul(0.3))

            // Feathered by the noise: where the mask is faint, the noise
            // decides whether this patch is grass or floor, which is what
            // turns a hard boundary into a scatter of green.
            const grass = smoothstep(float(0.12), float(0.55), red.mul(0.75).add(n.mul(0.45)).mul(step(0.02, red)))

            const dark = vec3(GRASS_DARK[0], GRASS_DARK[1], GRASS_DARK[2])
            const lightGreen = vec3(GRASS_LIGHT[0], GRASS_LIGHT[1], GRASS_LIGHT[2])
            const earth = mix(dark, lightGreen, n)

            // The grid goes with it. A meadow with survey lines ruled
            // across it is the one thing that would give the whole trick
            // away, so the lines fade out under the grass.
            const floorColor = mix(bg, ln, line.mul(float(1).sub(grass)))
            return mix(floorColor, earth, grass)
        })

        return gridNode()
    }, [gridSize, lineWidth, bgColor, lineColor])

    return { gridColorNode }
}

export function GridMaterial({ gridSize, lineWidth, bgColor, lineColor, roughness = 1, metalness = 0 }) {
    const { gridColorNode } = useGridMaterial({ gridSize, lineWidth, bgColor, lineColor })

    return (
        <meshStandardNodeMaterial
            colorNode={gridColorNode}
            roughness={roughness}
            metalness={metalness}
            side={THREE.DoubleSide}
        />
    )
}

